<template>
  <div class="lp-root">

    <!-- Navbar -->
    <header class="lp-navbar" :class="{ 'lp-navbar--scrolled': scrolled }">
      <div class="lp-container lp-navbar__inner">
        <router-link to="/">
          <img src="@/assets/images/logo/mexico_verde2.png" alt="Portal México Verde" class="lp-navbar__logo" />
        </router-link>
        <nav class="lp-navbar__links">
          <router-link to="/" class="lp-nav-link">Inicio</router-link>
          <a href="/#diagnostico" class="lp-nav-link lp-navbar__diag-link">Diagnóstico</a>
          <a href="/#blog" class="lp-nav-link">Blog</a>
        </nav>
        <div class="lp-navbar__actions">
          <router-link to="/auth/login" class="lp-btn lp-btn--outline-light">Ingresar</router-link>
          <router-link to="/auth/register" class="lp-btn lp-btn--primary">Registrarse</router-link>
        </div>
      </div>
    </header>

    <!-- Loading -->
    <div v-if="loading" class="lpb-state">
      <div class="lpb-spinner"></div>
      <p>Cargando artículo…</p>
    </div>

    <!-- No encontrado / sin caché -->
    <div v-else-if="notFound" class="lpb-state">
      <p class="lpb-error-msg">Este artículo no está disponible en este momento.</p>
      <p style="color:#999; font-size:.9rem;">Visita el blog desde la página principal para ver las entradas disponibles.</p>
      <router-link to="/#blog" class="lp-btn lp-btn--outline">← Ir al blog</router-link>
    </div>

    <template v-else-if="post">

      <!-- Hero del post -->
      <section class="lpb-hero">
        <div class="lp-container lpb-hero__inner">
          <router-link to="/#blog" class="lpb-back">← Volver al blog</router-link>
          <span class="lpb-date">{{ publishedAt }}</span>
          <h1 class="lpb-hero__title">{{ post.title }}</h1>
          <p v-if="post.intro && post.intro !== post.title" class="lpb-hero__intro">{{ post.intro }}</p>
        </div>
      </section>

      <!-- Imagen destacada -->
      <div v-if="coverImageUrl" class="lpb-cover">
        <div class="lp-container">
          <img :src="coverImageUrl" :alt="post.thumbnail_rendition?.alt || post.title" class="lpb-cover__img" />
        </div>
      </div>

      <!-- Cuerpo del artículo -->
      <main class="lpb-main">
        <div class="lp-container lpb-article-wrap">
          <article class="lpb-article">
            <div class="lpb-body" v-html="renderedBody"></div>
            <div v-if="post.footer" class="lpb-footer-note">
              <p>Para mayor información <span v-html="post.footer"></span></p>
            </div>
          </article>
        </div>
      </main>

      <!-- CTA: invitación a ingresar/registrarse -->
      <section class="lpb-cta">
        <div class="lp-container">
          <div class="lpb-cta__inner">
            <div class="lpb-cta__text">
              <span class="lp-eyebrow lp-eyebrow--light">Contenido exclusivo</span>
              <h2>¿Quieres acceder a más noticias y recursos?</h2>
              <p>Regístrate en Portal México Verde y obtén acceso completo a todas las publicaciones, herramientas de diagnóstico, directorios de servicios verdes y mucho más.</p>
              <ul class="lpb-cta__benefits">
                <li v-for="b in benefits" :key="b">{{ b }}</li>
              </ul>
            </div>
            <div class="lpb-cta__actions">
              <router-link to="/auth/register" class="lp-btn lp-btn--primary lp-btn--lg">
                Crear cuenta gratuita
              </router-link>
              <router-link to="/auth/login" class="lp-btn lp-btn--outline-light lp-btn--lg">
                Ya tengo cuenta →
              </router-link>
            </div>
          </div>
        </div>
      </section>

    </template>

    <!-- Footer -->
    <footer class="lp-footer">
      <div class="lp-container lp-footer__inner">
        <div class="lp-footer__brand">
          <p>Plataforma institucional para la transición hacia una economía verde en México.</p>
        </div>
        <div class="lp-footer__links">
          <h5>Plataforma</h5>
          <router-link to="/">Inicio</router-link>
          <a href="/#diagnostico">Diagnóstico gratuito</a>
          <a href="/#blog">Blog</a>
        </div>
        <div class="lp-footer__links">
          <h5>Acceso</h5>
          <router-link to="/auth/login">Ingresar</router-link>
          <router-link to="/auth/register">Registrarse</router-link>
        </div>
        <div class="lp-footer__links">
          <h5>Contacto</h5>
          <a href="mailto:contacto@mexicoverde.mx">contacto@mexicoverde.mx</a>
          <span>Región México</span>
        </div>
      </div>
      <div class="lp-footer__bottom">
        <span>© {{ currentYear }} Portal México Verde · AMSDE. Todos los derechos reservados.</span>
      </div>
    </footer>

  </div>
</template>

<script>
const BASE = 'https://rverdeadmin.klatus.systems';

export default {
  name: "BlogPostPublico",

  data() {
    return {
      post: null,
      renderedBody: null,
      loading: true,
      notFound: false,
      scrolled: false,
      currentYear: new Date().getFullYear(),
      benefits: [
        'Boletín informativo y noticias sobre economía verde',
        'Diagnóstico de reverdecimiento empresarial personalizado',
        'Hoja de ruta hacia la sostenibilidad',
        'Directorio de proveedores y servicios verdes',
        'Biblioteca de documentos técnicos y normativa',
        'Acceso a oportunidades de encadenamiento e inversión',
      ],
    };
  },

  computed: {
    publishedAt() {
      const d = this.post?.meta?.first_published_at;
      return d
        ? new Date(d).toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' })
        : null;
    },
    coverImageUrl() {
      const r = this.post?.thumbnail_rendition;
      if (!r) return null;
      const url = r.full_url || r.url || '';
      return url.startsWith('http') ? url : `${BASE}${url}`;
    },
  },

  created() {
    this.loadFromCache();
  },

  mounted() {
    window.addEventListener('scroll', this.onScroll);
  },
  beforeUnmount() {
    window.removeEventListener('scroll', this.onScroll);
  },

  methods: {
    onScroll() {
      this.scrolled = window.scrollY > 60;
    },

    loadFromCache() {
      this.loading = true;
      try {
        const { id } = this.$route.params;
        const raw = sessionStorage.getItem('_blog_cache');
        if (!raw) { this.notFound = true; return; }

        const posts = JSON.parse(raw);
        const post  = posts.find((p) => String(p.id) === String(id));
        if (!post)  { this.notFound = true; return; }

        this.post = post;
        this.renderedBody = this.parseBody(post.body);
      } catch {
        this.notFound = true;
      } finally {
        this.loading = false;
      }
    },

    // Maneja tanto RichTextField (string HTML) como StreamField (array de bloques)
    parseBody(body) {
      if (!body) return '';

      if (typeof body === 'string') {
        // Elimina <embed embedtype="image"> que requieren auth para resolverse
        return body.replace(/<embed[^>]*embedtype="image"[^/]*/gi, '');
      }

      if (Array.isArray(body)) {
        return body.map((block) => {
          switch (block.type) {
            case 'paragraph': return block.value ?? '';
            case 'heading':   return `<h3 class="lpb-block-heading">${block.value ?? ''}</h3>`;
            case 'image':     return ''; // no se puede resolver sin auth
            default:
              return typeof block.value === 'string'
                ? `<p>${block.value}</p>`
                : '';
          }
        }).join('\n');
      }

      return '';
    },
  },
};
</script>

<style scoped>
/* ── Shared landing tokens ─────────────────────────────────────────────── */
.lp-root { font-family: 'Segoe UI', system-ui, sans-serif; color: #1a1a2e; min-height: 100vh; display: flex; flex-direction: column; }
.lp-container { max-width: 1140px; margin: 0 auto; padding: 0 1.5rem; }

/* ── Buttons ───────────────────────────────────────────────────────────── */
.lp-btn {
  display: inline-flex; align-items: center; gap: .4rem;
  padding: .6rem 1.4rem; border-radius: 4px; font-weight: 600;
  font-size: .9rem; text-decoration: none; transition: all .2s;
  border: 2px solid transparent; cursor: pointer;
}
.lp-btn--primary        { background: #AF1F23; color: #fff; border-color: #AF1F23; }
.lp-btn--primary:hover  { background: #8e1519; border-color: #8e1519; }
.lp-btn--outline        { background: transparent; color: #09377c; border-color: #09377c; }
.lp-btn--outline:hover  { background: #09377c; color: #fff; }
.lp-btn--outline-light        { background: transparent; color: #fff; border-color: rgba(255,255,255,.6); }
.lp-btn--outline-light:hover  { background: rgba(255,255,255,.12); }
.lp-btn--lg { padding: .8rem 2rem; font-size: 1rem; }

/* ── Eyebrow ───────────────────────────────────────────────────────────── */
.lp-eyebrow {
  display: block; text-transform: uppercase; letter-spacing: .12em;
  font-size: .75rem; font-weight: 700; color: #AF1F23; margin-bottom: .5rem;
}
.lp-eyebrow--light { color: #81c784; }

/* ── Navbar ────────────────────────────────────────────────────────────── */
.lp-navbar {
  position: fixed; top: 0; left: 0; right: 0; z-index: 1000;
  background: transparent; transition: background .3s, box-shadow .3s;
}
.lp-navbar--scrolled { background: #09377c; box-shadow: 0 2px 16px rgba(0,0,0,.25); }
.lp-navbar__inner {
  display: flex; align-items: center; gap: 2rem; height: 68px;
}
.lp-navbar__logo { height: 34px; object-fit: contain; filter: brightness(0) invert(1); }
.lp-navbar__links { display: flex; gap: 1.8rem; margin-left: auto; }
.lp-nav-link { color: rgba(255,255,255,.85); font-size: .9rem; text-decoration: none; font-weight: 500; transition: color .2s; }
.lp-nav-link:hover { color: #fff; }
.lp-navbar__diag-link { color: #81c784 !important; font-weight: 600 !important; }
.lp-navbar__actions { display: flex; gap: .75rem; margin-left: 1.5rem; }

/* ── Loading / error state ─────────────────────────────────────────────── */
.lpb-state {
  flex: 1; display: flex; flex-direction: column; align-items: center;
  justify-content: center; gap: 1.5rem; padding: 8rem 1.5rem 4rem; text-align: center; color: #666;
}
.lpb-spinner {
  width: 40px; height: 40px; border: 3px solid #e2e8f0;
  border-top-color: #09377c; border-radius: 50%;
  animation: lpbSpin .8s linear infinite;
}
@keyframes lpbSpin { to { transform: rotate(360deg); } }
.lpb-error-msg { font-size: 1rem; color: #c62828; }

/* ── Hero ──────────────────────────────────────────────────────────────── */
.lpb-hero {
  background: linear-gradient(135deg, #0b1f3a 0%, #09377c 55%, #1a5276 100%);
  padding: 7rem 0 3.5rem;
}
.lpb-hero__inner { max-width: 820px; }
.lpb-back {
  display: inline-block; color: rgba(255,255,255,.65); font-size: .85rem;
  text-decoration: none; margin-bottom: 1.25rem; transition: color .2s;
}
.lpb-back:hover { color: #fff; }
.lpb-date {
  display: block; font-size: .75rem; font-weight: 700; color: #81c784;
  text-transform: uppercase; letter-spacing: .1em; margin-bottom: .75rem;
}
.lpb-hero__title {
  font-size: clamp(1.6rem, 3.5vw, 2.6rem); font-weight: 800;
  color: #fff; line-height: 1.2; margin: 0 0 1rem;
}
.lpb-hero__intro {
  font-size: 1.1rem; color: rgba(255,255,255,.72); line-height: 1.7; margin: 0;
}

/* ── Cover image ───────────────────────────────────────────────────────── */
.lpb-cover { background: #f5f7fa; padding: 0; }
.lpb-cover__img {
  width: 100%; max-height: 440px; object-fit: cover;
  display: block; border-radius: 0 0 12px 12px;
  box-shadow: 0 4px 24px rgba(9,55,124,.1);
}

/* ── Main content ──────────────────────────────────────────────────────── */
.lpb-main { background: #f5f7fa; padding: 3.5rem 0 4rem; flex-grow: 1; }
.lpb-article-wrap { max-width: 820px; margin: 0 auto; }
.lpb-article {
  background: #fff; border-radius: 10px; padding: 3rem;
  box-shadow: 0 4px 24px rgba(9,55,124,.07);
}

/* ── Article body typography ───────────────────────────────────────────── */
.lpb-body { line-height: 1.8; color: #2d2d2d; font-size: 1rem; }
.lpb-body :deep(h1),
.lpb-body :deep(h2),
.lpb-body :deep(h3),
.lpb-body :deep(h4) {
  color: #09377c; margin: 2rem 0 .75rem; font-weight: 700; line-height: 1.3;
}
.lpb-body :deep(h2) { font-size: 1.45rem; }
.lpb-body :deep(h3) { font-size: 1.2rem; }
.lpb-body :deep(p)  { margin: 0 0 1.25rem; }
.lpb-body :deep(a)  { color: #AF1F23; text-decoration: underline; }
.lpb-body :deep(ul),
.lpb-body :deep(ol) { padding-left: 1.5rem; margin-bottom: 1.25rem; }
.lpb-body :deep(li) { margin-bottom: .4rem; }
.lpb-body :deep(blockquote) {
  border-left: 4px solid #AF1F23; margin: 1.5rem 0; padding: .75rem 1.5rem;
  background: #fdecea; border-radius: 0 6px 6px 0; color: #555;
}
.lpb-body :deep(img),
.lpb-body-img {
  max-width: 100%; height: auto; border-radius: 8px; margin: 1.5rem auto; display: block;
  box-shadow: 0 2px 12px rgba(0,0,0,.08);
}

.lpb-footer-note {
  margin-top: 2rem; padding-top: 1.5rem; border-top: 1px solid #e2e8f0;
  font-size: .88rem; color: #666;
}

/* ── CTA section ───────────────────────────────────────────────────────── */
.lpb-cta {
  background: linear-gradient(135deg, #071628 0%, #0b2a1a 100%);
  padding: 5rem 0;
}
.lpb-cta__inner {
  display: grid; grid-template-columns: 1fr auto; gap: 4rem; align-items: center;
}
.lpb-cta__text h2 {
  color: #fff; font-size: clamp(1.4rem, 2.5vw, 2rem); font-weight: 700; margin: .5rem 0 1rem;
}
.lpb-cta__text > p { color: rgba(255,255,255,.72); line-height: 1.7; margin-bottom: 1.5rem; }
.lpb-cta__benefits { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: .6rem; }
.lpb-cta__benefits li {
  color: rgba(255,255,255,.8); font-size: .9rem;
  padding-left: 1.4rem; position: relative;
}
.lpb-cta__benefits li::before { content: '✓'; position: absolute; left: 0; color: #66bb6a; font-weight: 700; }

.lpb-cta__actions {
  display: flex; flex-direction: column; gap: 1rem;
  align-items: stretch; min-width: 220px;
}

/* ── Footer ────────────────────────────────────────────────────────────── */
.lp-footer { background: #0b1f3a; color: rgba(255,255,255,.7); padding: 4rem 0 0; }
.lp-footer__inner {
  display: grid; grid-template-columns: 2fr 1fr 1fr 1fr; gap: 2.5rem; padding-bottom: 3rem;
}
.lp-footer__brand p { font-size: .85rem; line-height: 1.7; }
.lp-footer__links { display: flex; flex-direction: column; gap: .6rem; }
.lp-footer__links h5 {
  color: #fff; font-size: .85rem; font-weight: 700;
  text-transform: uppercase; letter-spacing: .08em; margin: 0 0 .5rem;
}
.lp-footer__links a,
.lp-footer__links span { font-size: .85rem; color: rgba(255,255,255,.55); text-decoration: none; transition: color .2s; }
.lp-footer__links a:hover { color: #fff; }
.lp-footer__bottom {
  border-top: 1px solid rgba(255,255,255,.08); padding: 1.25rem 1.5rem;
  text-align: center; font-size: .8rem; color: rgba(255,255,255,.35);
}

/* ── Responsive ────────────────────────────────────────────────────────── */
@media (max-width: 768px) {
  .lp-navbar__links,
  .lp-navbar__actions { display: none; }

  .lpb-article { padding: 1.75rem 1.25rem; }
  .lpb-cta__inner { grid-template-columns: 1fr; gap: 2.5rem; }
  .lpb-cta__actions { flex-direction: row; flex-wrap: wrap; }

  .lp-footer__inner { grid-template-columns: 1fr 1fr; }
}
@media (max-width: 480px) {
  .lp-footer__inner { grid-template-columns: 1fr; }
  .lpb-cta__actions { flex-direction: column; }
}
</style>